import React, { useMemo } from 'react'
import { motion } from 'framer-motion'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { AddTask } from '../../../../components/AddTask'
import { TaskEntry } from '../../../../components/TaskEntry'
import type { TaskEntryData } from '../../../../components/TaskEntry'
import { taskToEntryData } from '../../../../utils/taskToEntryData'
import { TaskList } from '../../../../components/TaskList'
import { SectionHeader } from '../../../../components/SectionHeader'
import { SectionDropZone } from '../../../../components/SectionDropZone'
import { SECTION_ID_PREFIX, EMPTY_SECTION_PREFIX } from '../../../../hooks/useSectionDrag'
import type { ProjectSegmentProps } from './ProjectSegment.types'

/** Invisible drop target for empty sections — allows tasks to be dragged into them.
 *  0-height normally; expands to 32px only when a task drag is active. */
const EmptySectionDropZone: React.FC<{ sectionId: string; active: boolean }> = ({ sectionId, active }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `${EMPTY_SECTION_PREFIX}${sectionId}`,
    disabled: !active,
  })
  return (
    <div
      ref={setNodeRef}
      className={`empty-section-drop-zone${active ? ' empty-section-drop-zone--active' : ''}${isOver ? ' empty-section-drop-zone--over' : ''}`}
    />
  )
}

/** Wrapper that registers a SectionHeader as a dnd-kit draggable node. */
const DraggableSectionHeader: React.FC<
  React.ComponentProps<typeof SectionHeader>
> = (props) => {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `${SECTION_ID_PREFIX}${props.id}`,
  })
  return (
    <SectionHeader
      {...props}
      dragRef={setNodeRef}
      dragAttributes={attributes}
      dragListeners={listeners}
    />
  )
}

export const ProjectSegment: React.FC<ProjectSegmentProps> = ({
  segment, index: i, isActive, showAddTask, savedName, totalSegments, projectId,
  gapAfterSegmentIdx, gapHeight,
  activeSectionDragId, dragSourceSegIdx,
  autoEditSectionId, getSectionProps,
  handleToggleCollapse, handleRenameSection, handleDeleteSection, sectionsReadonly,
  selectSection, selectSectionTasks, handleSectionNavigateDown, handleSectionNavigateUp,
  creatingSegmentIdx, creatingPosition, morphData, positionMorphKey, inlineMorphTitle, inlineMorphSelected, inlineMorphKey, creationKey, inlineResetKey, skipInlineEntrance,
  autoSelectId, creatingAfterTaskId, pendingInlineTarget, topCreateRef, bottomCreateRef,
  startCreating, handleAddSection,
  handleCreateTask, handleCreateTaskAndContinue,
  handleCreateTaskAfter, handleCreateTaskAfterAndContinue,
  handleCancelCreate, handleMorphComplete, handleInlineMorphComplete,
  handleToggleComplete, handleDeleteTask, handleDuplicate, handleContextMenuBadgeUpdate, handleUpdateTask, handleUpdateTaskAndCreateBelow,
  navigateEdit, getExternalSelection,
  getSegmentHandleRef, taskListHandleRef,
  draggedIds, activeDragId, dragCount, isCrossSegmentDrag, optimisticUpdates,
  setHoveredSegmentIndex, setHoveringTasks,
  inProjectContext,
}) => {
  const segHasTasks = segment.tasks.length > 0
  // Mount-stable list — when collapsed, `segment.tasks` is [] but `allTasks`
  // still carries the full list so TaskList stays mounted (CSS-hidden).
  const mountedTasks = segment.allTasks ?? segment.tasks
  const hasMountedTasks = mountedTasks.length > 0

  // `taskToEntryData` is keyed off the Task reference via a global
  // WeakMap, so unchanged Tasks come out as the same TaskEntryData ref —
  // SortableTaskEntry/TaskEntry memo bails for those rows. It also wires
  // up derived flags (`dormant`, `completed`) and the projectId /
  // lifeSpace passthrough that TaskEntry's dormant treatment + reactivate
  // dropdown depend on. The previous local-cache + spread skipped those
  // derived flags, which is why dormant tasks rendered without the
  // dashed-arc check or the stone background tint.
  const taskEntryList = useMemo<TaskEntryData[]>(
    () => mountedTasks.map(taskToEntryData),
    [mountedTasks],
  )
  const isSectionDragging = !!activeSectionDragId && segment.section?.id === activeSectionDragId

  // First task in a section: the standalone creator/morph entry needs margin-top
  // matching .task-list (12px) so the task doesn't jump when it moves into the
  // task-list after morph completes.
  // Without this, section-header margin-bottom (4px) is the only gap, but the
  // task-list uses margin-top: 12px — causing an 8px downward jump at morph end.
  // Bottom position: keep margin during morph even after TaskList renders,
  // because the morph entry sits below the TaskList and needs its own spacing.
  const firstTaskMargin = (pos: 'top' | 'bottom') => {
    if (!segment.section) return undefined
    if (!segHasTasks) return { marginTop: 12 }
    // Bottom morph in a section that just got its first task: the new task is
    // excluded from TaskList (via excludeTaskId), so visually the list is empty.
    // Keep 12px so the morph-to-list swap is seamless.
    if (pos === 'bottom' && segment.tasks.length === 1 && morphData?.position === pos && morphData.segmentIdx === i) {
      return { marginTop: 12 }
    }
    return undefined
  }

  /** Shared two-element morph + creator blocks for top/bottom positions. */
  const renderPositionCreator = (pos: 'top' | 'bottom', createRef: React.RefObject<HTMLDivElement | null>) => (
    <>
      {/* Two-element morph — morph element (old key, creating→collapsed) */}
      {positionMorphKey != null && morphData?.position === pos && morphData.segmentIdx === i && (
        <motion.div
          key={`create-${pos}-wrap-${positionMorphKey}`}
          initial={false}
          animate={{ opacity: 1 }}
          style={firstTaskMargin(pos)}
        >
          <TaskEntry
            key={`create-${pos}-${positionMorphKey}`}
            mode="collapsed"
            task={{ id: 'position-morphing', title: morphData.title, completed: false }}
            selected={morphData.selected}
            onMorphComplete={handleMorphComplete}
          />
        </motion.div>
      )}
      {/* Creator block (handles initial, CMD+Enter continue, and Enter-stop morph) */}
      {((creatingSegmentIdx === i && creatingPosition === pos) || (positionMorphKey == null && !creatingPosition && morphData?.position === pos && morphData.segmentIdx === i)) && (
        <motion.div
          key={`create-${pos}-wrap-${creationKey}`}
          ref={creatingPosition === pos ? createRef : undefined}
          initial={positionMorphKey != null ? { opacity: 0, height: 0, overflow: 'clip' as const } : false}
          animate={{ opacity: 1, height: 'auto', overflow: 'clip' as const, transitionEnd: { overflow: 'visible' as const } }}
          transition={{
            height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
            opacity: { duration: 0.2, delay: 0.05 },
          }}
          style={pos === 'top' && positionMorphKey != null ? { marginTop: 2 } : firstTaskMargin(pos)}
        >
          <TaskEntry
            key={`create-${pos}-${creationKey}`}
            mode={morphData?.position === pos && !creatingPosition ? 'collapsed' : 'creating'}
            task={morphData?.position === pos && !creatingPosition ? { id: 'morphing', title: morphData.title, completed: false } : undefined}
            selected={morphData?.position === pos && !creatingPosition ? morphData.selected : false}
            onSubmitCreate={creatingPosition === pos ? handleCreateTask : undefined}
            onSubmitCreateAndContinue={creatingPosition === pos ? handleCreateTaskAndContinue : undefined}
            onCancel={creatingPosition === pos ? handleCancelCreate : undefined}
            autoFocus={creatingPosition === pos}
            onMorphComplete={morphData?.position === pos && !creatingPosition ? handleMorphComplete : undefined}
          />
        </motion.div>
      )}
    </>
  )

  // Disable drop zone immediately after source (redundant — same position as source)
  const isDropZoneActive = !!activeSectionDragId && i !== dragSourceSegIdx + 1

  return (
    <React.Fragment>
      {/* Section drop zone before this segment (only for segments with sections) */}
      {segment.section && (
        <SectionDropZone index={i} active={isDropZoneActive} />
      )}
      <div
        data-segment-idx={i}
        data-section-id={segment.section?.id ?? undefined}
        className={`project-page__segment${gapAfterSegmentIdx !== null && i === gapAfterSegmentIdx ? ' project-page__segment--gap-below' : ''}${isSectionDragging ? ' project-page__segment--section-dragging' : ''}`}
        style={gapAfterSegmentIdx !== null && i === gapAfterSegmentIdx ? { '--drag-gap-height': `${gapHeight}px` } as React.CSSProperties : undefined}
        onMouseMove={(e) => {
          setHoveredSegmentIndex(i)
          // Hide AddTask when hovering over section headers or open menus
          const target = e.target as HTMLElement
          setHoveringTasks(!target.closest('.section-header') && !target.closest('[role="menu"]'))
        }}
      >
        {/* AddTask above the first section header — add task or section (unsectioned).
            Hidden for readonly-section contexts (e.g. Quick Notes): virtual buckets
            don't support unsectioned-above creation. */}
        {i === 0 && segment.section && !segment.hideHeader && !sectionsReadonly && savedName.trim() && (
          <div className={`project-page__add-task-above${creatingPosition || morphData ? ' project-page__add-task-above--creating' : ''}`} onPointerUp={(e) => { if ((e.target as HTMLElement).closest('[role="menu"]') || creatingPosition) return; startCreating('above', i) }}>
            <AddTask
              onAddTask={() => startCreating('above', i)}
              onAddSection={() => handleAddSection('top', segment.section!.id, 'before')}
              shortcutKey="F"
              disabled={!!creatingPosition || !!creatingAfterTaskId}
            />
          </div>
        )}
        {/* 'above' TaskEntry (creation + morph) is rendered OUTSIDE the segment map
            in ProjectPage.tsx so segment key changes don't break the morph transition. */}

        {segment.section && !segment.hideHeader && (
          <DraggableSectionHeader
            id={segment.section.id}
            title={segment.section.title}
            taskCount={segment.taskCount}
            collapsed={segment.section.collapsed}
            autoEdit={autoEditSectionId === segment.section.id}
            readonly={sectionsReadonly}
            {...getSectionProps(segment.section.id)}
            onToggleCollapse={handleToggleCollapse}
            onRename={handleRenameSection}
            onDelete={handleDeleteSection}
            onSelect={selectSection}
            onSelectAllTasks={selectSectionTasks}
            onNavigateDown={() => handleSectionNavigateDown(segment.section!.id)}
            onNavigateUp={() => handleSectionNavigateUp(segment.section!.id)}
          />
        )}

        {/* AddTask (A) — after section header, before tasks (only when segment has tasks).
            Always rendered so CSS transitions work; visibility via --active class. */}
        {segHasTasks && savedName.trim() && (
          <div className={`project-page__add-task-top${creatingPosition || morphData ? ' project-page__add-task-top--creating' : ''}`} onPointerUp={(e) => { if ((e.target as HTMLElement).closest('[role="menu"]') || creatingPosition) return; startCreating('top', i) }}>
            <AddTask
              onAddTask={() => startCreating('top', i)}
              onAddSection={() => handleAddSection('top', segment.section ? segment.section.id : segment.tasks[0].id, 'before')}
              shortcutKey="A"
              disabled={!!creatingPosition || !!creatingAfterTaskId}
            />
          </div>
        )}
        {renderPositionCreator('top', topCreateRef)}

        {hasMountedTasks && (
          <div hidden={segment.collapsed}>
          <TaskList
            skipSortableContext
            inProjectContext={inProjectContext}
            tasks={taskEntryList}
            onToggleComplete={handleToggleComplete}
            onDelete={handleDeleteTask}
            onDuplicate={handleDuplicate}
            onContextMenuBadgeUpdate={handleContextMenuBadgeUpdate}
            onUpdateTask={handleUpdateTask}
            onUpdateTaskAndCreateBelow={handleUpdateTaskAndCreateBelow}
            onNavigateEdit={navigateEdit}
            stateKey={`${projectId}-${segment.section?.id ?? 'none'}`}
            handleRef={segment.section ? getSegmentHandleRef(segment.section.id) : taskListHandleRef}
            externalSelection={getExternalSelection(segment.tasks.map(t => t.id))}
            excludeTaskId={(morphData || inlineMorphTitle) ? autoSelectId ?? undefined : undefined}
            autoSelectId={(!morphData && !inlineMorphTitle) ? autoSelectId ?? undefined : undefined}
            inlineCreation={creatingAfterTaskId && segment.tasks.some(t => t.id === creatingAfterTaskId) ? {
              afterTaskId: creatingAfterTaskId,
              key: creationKey,
              resetKey: inlineResetKey,
              morphTitle: inlineMorphTitle ?? undefined,
              morphSelected: inlineMorphSelected,
              morphKey: inlineMorphKey ?? undefined,
              onSubmitCreate: handleCreateTaskAfter,
              onSubmitCreateAndContinue: handleCreateTaskAfterAndContinue,
              onCancel: handleCancelCreate,
              onMorphComplete: handleInlineMorphComplete,
              skipEntrance: skipInlineEntrance || undefined,
            } : null}
            draggedIds={draggedIds}
            activeDragId={activeDragId}
            dragCount={dragCount || undefined}
            isCrossSegmentDrag={isCrossSegmentDrag}
            skipEntrance={!!optimisticUpdates}
            initialEnteringId={(() => {
              if (segment.section) return undefined
              const target = creatingAfterTaskId || pendingInlineTarget
              return target && segment.tasks.some(t => t.id === target) ? target : undefined
            })()}
          />
          </div>
        )}

        {/* Droppable zone for empty sections — enables task drag-and-drop into sections with 0 tasks */}
        {!segHasTasks && segment.section && !segment.hideHeader && !segment.section.collapsed && (
          <EmptySectionDropZone sectionId={segment.section.id} active={!!activeDragId && !activeSectionDragId} />
        )}

        {/* AddTask (B) — after tasks, after empty uncollapsed sections, or after the last collapsed section (section-only).
            Hidden between collapsed sections (no task creation there). */}
        {savedName.trim() && (segHasTasks || !segment.section?.collapsed || i === totalSegments - 1) && (() => {
          const isSectionOnly = !segHasTasks && !!segment.section?.collapsed
          return (
            <div className={`project-page__add-task-bottom${!segHasTasks && !creatingPosition ? ' project-page__add-task-bottom--empty' : ''}${creatingPosition || morphData ? ' project-page__add-task-bottom--creating' : ''}`} onPointerUp={(e) => { if ((e.target as HTMLElement).closest('[role="menu"]') || creatingPosition) return; isSectionOnly ? handleAddSection('bottom', segment.section!.id, 'after') : startCreating('bottom', i) }}>
              <AddTask
                onAddTask={() => startCreating('bottom', i)}
                onAddSection={() => segHasTasks ? handleAddSection('bottom', segment.tasks[segment.tasks.length - 1].id, 'after') : handleAddSection('bottom', segment.section!.id, 'before')}
                shortcutKey={isSectionOnly ? 'S' : (segHasTasks ? 'B' : 'A')}
                sectionOnly={isSectionOnly}
                disabled={!!creatingPosition || !!creatingAfterTaskId}
              />
            </div>
          )
        })()}
        {renderPositionCreator('bottom', bottomCreateRef)}
      </div>
    </React.Fragment>
  )
}
